import { Button, ButtonTheme } from 'components/Button';
import { HeaderToolbar } from 'components/HeaderToolbar';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Menu } from 'components/Menu';
import { ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useScreenBreakpoints } from 'hooks/useScreenBreakpoints';
import { useSelector } from 'hooks/useSelector';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { pluralise } from 'lib/pluralise';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import { sortBy } from 'lodash';
import React from 'react';
import { View } from 'react-native';

import { useDispatch } from 'react-redux';
import {
  doAsesListLoad,
  doAsesListFilterOpenModal,
  doAsesListFilterOpenOrganisationSelect,
  doAsesListSetFilter,
  eventSelectScreenSelection,
  NAVIGATE_MODAL,
} from 'store/actions';
import {
  selectAsesListFilter,
  selectAsesListFilterOptions,
  selectAsesListFilterSearchTerm,
  selectAsesListFilterStatuses,
  selectAsesListTotalCount,
  selectAsesListFilterEverLoaded,
  selectAsesListEverLoaded,
} from 'store/ases/selectors';
import { ListFilter } from 'store/ases/types';
import { AssessmentStatusesOrder } from 'types/types';

const screenBreakpoints = [
  {
    width: 450,
    key: 1,
  },
  {
    width: 550,
    key: 2,
  },
  {
    width: 700,
    key: 3,
  },
  {
    width: 850,
    key: 4,
  },
];

export const AssessmentListHeader: React.FC = () => {
  const dispatch = useDispatch();

  const screenBreakpoint = useScreenBreakpoints(screenBreakpoints) || 0;

  const renderCountRef = React.useRef(0);
  renderCountRef.current += 1;

  const listEverLoaded = useSelector(selectAsesListEverLoaded);
  const isListFilterLoaded = useSelector(selectAsesListFilterEverLoaded);
  const listTotalCount = useSelector(selectAsesListTotalCount);
  const searchValue = useSelector(selectAsesListFilterSearchTerm);
  const filterOptions = useSelector(selectAsesListFilterOptions);
  const filterValues = useSelector(selectAsesListFilter);
  const statusesValue = useSelector(selectAsesListFilterStatuses);
  const searchValueRef = React.useRef(searchValue);

  const handleFilter = React.useCallback(
    (filterChanges: Partial<ListFilter>) => {
      dispatch(
        doAsesListSetFilter({
          data: { ...filterChanges },
          append: true,
          loadList: {
            useBlockingLoader: false,
          },
        }),
      );
    },
    [dispatch],
  );

  const handleSearch = React.useCallback(
    (newSearchValue: string) => {
      searchValueRef.current = newSearchValue;
      handleFilter({ searchTerm: newSearchValue });
    },
    [handleFilter],
  );

  // Check if search value has changed, provide a new key if it has
  const textfieldKeyRef = React.useRef(randomKey());
  if (searchValueRef.current !== searchValue) {
    textfieldKeyRef.current = randomKey();
    searchValueRef.current = searchValue;
  }

  const statusesText = React.useMemo<string>(() => {
    const thsStatusesText: string[] = filterOptions.statuses
      .filter((s) => statusesValue.includes(s.id))
      .map((s) => s.name);
    return thsStatusesText.length &&
      thsStatusesText.length !== AssessmentStatusesOrder.length
      ? `${thsStatusesText[0]}` +
          `${pluralise(thsStatusesText.length - 1, '+1', '+{x}', '')}`
      : '';
  }, [filterOptions.statuses, statusesValue]);

  const handleOpenAssetGroups = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        size: 'small',
        params: {
          title: 'Property Groups',
          options: filterOptions.propertyGroups.map((o) => ({
            internalValue: o.id,
            name: o.name,
            groupName: o.organisationName,
          })),
          multiSelect: true,
          searchable: true,
          showQuickChoose: true,
          eventKey: 'asesListHeaderFilterPropertyGroups',
          value: filterValues.propertyGroupIds,
        },
      }),
    );

  const handleOpenAssessmentTypes = () =>
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.SELECT,
        size: 'small',
        params: {
          title: 'Assessment Types',
          options: sortBy(filterOptions.assessmentTypes, 'name').map((o) => ({
            internalValue: o.id,
            name: o.name,
          })),
          multiSelect: true,
          searchable: true,
          showQuickChoose: true,
          eventKey: 'asesListHeaderFilterAssessmentTypes',
          value: filterValues.assessmentTypes,
        },
      }),
    );

  React.useEffect(() => {
    const events = [
      doAddEvent(eventSelectScreenSelection, (action) => {
        if (action.payload.key === 'asesListHeaderFilterAssessmentTypes') {
          handleFilter({
            assessmentTypes: action.payload.stringValues,
          });
        } else if (
          action.payload.key === 'asesListHeaderFilterPropertyGroups'
        ) {
          handleFilter({
            propertyGroupIds: action.payload.stringValues,
          });
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch, handleFilter]);

  return (
    <View>
      <NavigationHeader marginRight={0} marginLeft={0}>
        <ViewWithSeparators
          horizontal
          style={{
            alignItems: 'center',
            flex: 1,
          }}
          separatorMargin={theme.paddingHorizontalHalf}
          separatorColor="transparent"
        >
          <Txt
            weight="bold"
            numberOfLines={1}
            style={{ flex: 1, paddingLeft: theme.paddingHorizontal }}
          >
            Assessments{' '}
            {listEverLoaded && (
              <Txt
                numberOfLines={1}
                style={{ color: theme.colors.fieldValueText }}
              >
                {pluralise(
                  listTotalCount,
                  '{x} Assessment',
                  '{x} Assessments',
                  'No Assessments',
                )}
              </Txt>
            )}
          </Txt>
          <Menu
            iconButtonConfig={{ icon: ICONS.EllipsisLight }}
            options={[
              // {
              //   text: 'Export List as CSV',
              //   icon: ICONS.ArrowDownLight,
              // },
              // '-',
              {
                text: 'Reload',
                icon: ICONS.RotateRightLight,
                onPress: () =>
                  dispatch(
                    doAsesListLoad({
                      append: false,
                      useDraftFilter: false,
                      useBlockingLoader: false,
                      type: 'filter',
                    }),
                  ),
              },
            ]}
          />
        </ViewWithSeparators>
      </NavigationHeader>

      <HeaderToolbar
        searchValue={searchValue}
        onSearch={handleSearch}
        searchPlaceholder={
          filterOptions.organisationRequired
            ? 'Filter to enable search'
            : 'Search Assessments'
        }
        searchDisabled={filterOptions.organisationRequired}
      >
        {isListFilterLoaded && screenBreakpoint > 0 && (
          <Menu
            buttonConfig={{
              text: statusesText || 'All Statuses',
              iconRight: ICONS.CaretDownSolid,
              iconRightSize: 12,
              textSize: 'xsmall',
              buttonTheme: ButtonTheme.themeless,
              chipLike: true,
            }}
            width={140}
            options={[
              ...filterOptions.statuses.map((status) => ({
                text: status.name,
                icon: ICONS.FileDuotone,
                iconColor: `#${status.colour}`,
                onPress: () => {
                  handleFilter({ statuses: [status.id] });
                },
              })),
              '-',
              {
                text: 'All Statuses',
                onPress: () => {
                  handleFilter({ statuses: [] });
                },
              },
            ]}
          />
        )}
        {isListFilterLoaded &&
          screenBreakpoint > 1 &&
          filterOptions.organisations.length > 1 && (
            <Button
              text={`${
                filterValues.organisationIds.length === 0
                  ? 'All Orgs'
                  : filterValues.organisationIds.length === 1
                    ? `${
                        filterOptions.organisations.find(
                          (org) => filterValues.organisationIds[0] === org.id,
                        )?.name
                      }`
                    : `${filterValues.organisationIds.length} Orgs`
              }`}
              style={{ maxWidth: 150 }}
              iconRight={ICONS.CaretDownSolid}
              iconRightSize={12}
              buttonTheme={ButtonTheme.themeless}
              textSize="xsmall"
              chipLike
              onPress={() =>
                dispatch(
                  doAsesListFilterOpenOrganisationSelect({
                    getDraft: false,
                    openFilterAfterSelect: true,
                  }),
                )
              }
            />
          )}
        {isListFilterLoaded &&
          screenBreakpoint > 2 &&
          filterOptions.assessmentTypes.length > 1 && (
            <Button
              text={`${
                filterValues.assessmentTypes.length === 0
                  ? 'All Types'
                  : filterValues.assessmentTypes.length === 1
                    ? `${
                        filterOptions.assessmentTypes.find(
                          (org) => filterValues.assessmentTypes[0] === org.id,
                        )?.name
                      }`
                    : `${filterValues.assessmentTypes.length} Types`
              }`}
              style={{ maxWidth: 150 }}
              iconRight={ICONS.CaretDownSolid}
              iconRightSize={12}
              buttonTheme={ButtonTheme.themeless}
              textSize="xsmall"
              chipLike
              onPress={handleOpenAssessmentTypes}
            />
          )}
        {isListFilterLoaded &&
          screenBreakpoint > 3 &&
          filterOptions.propertyGroups?.length > 1 && (
            <Button
              text={`${
                filterValues.propertyGroupIds.length === 0
                  ? 'All Groups'
                  : filterValues.propertyGroupIds.length === 1
                    ? `${
                        filterOptions.propertyGroups.find(
                          (assetGroup) =>
                            filterValues.propertyGroupIds[0] === assetGroup.id,
                        )?.name
                      }`
                    : `${filterValues.propertyGroupIds.length} Groups`
              }`}
              style={{ maxWidth: 150 }}
              iconRight={ICONS.CaretDownSolid}
              iconRightSize={12}
              buttonTheme={ButtonTheme.themeless}
              textSize="xsmall"
              chipLike
              onPress={handleOpenAssetGroups}
            />
          )}
        <IconButton
          icon={ICONS.SlidersUpLight}
          onPress={() =>
            dispatch(doAsesListFilterOpenModal({ setDraft: false }))
          }
          showBadge={
            !!filterValues.organisationIds.length ||
            !!filterValues.assessmentTypes.length ||
            !!filterValues.propertyGroupIds.length ||
            !!filterValues.flagsSet.length ||
            !!filterValues.flagsNotSet.length ||
            !!statusesText
          }
        />
      </HeaderToolbar>
    </View>
  );
};
